import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash,
  faEdit,
  faSync,
  faFolderOpen,
  faSpinner,
  faSitemap,
  faTimes,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import './CategoriasData.css';
import baseURL from '../../url';
import NewCategoria from '../NewCategoria/NewCategoria';

// Custom Toast Component
const CustomToast = ({ toasts }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="product-toast-container">
      {toasts.map(toast => (
        <div key={toast.id} className={`product-toast ${toast.type}`}>
          <div className="toast-icon">
            {toast.type === 'success' && '✅'}
            {toast.type === 'error' && '❌'}
            {toast.type === 'warning' && '⚠️'}
            {toast.type === 'info' && 'ℹ️'}
          </div>
          <div className="toast-content">
            <div className="toast-title">{toast.message}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function CategoriasData() {
  // === State ===
  const [categories, setCategories] = useState([]);              // <- antes "categorias"
  const [modalVisible, setModalVisible] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [activeCategory, setActiveCategory] = useState({});
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);

  // subcategorías (para modal)
  const [subcategories, setSubcategories] = useState([]);
  const [newSubcategory, setNewSubcategory] = useState('');

  // === Effects ===
  useEffect(() => {
    loadCategories();
  }, []);

  // === Helpers ===
  const showToast = (message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  };

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseURL}/categoriasGet.php`, { method: 'GET' });
      const data = await res.json();

      // Esperado: { success: true, categorias: [{ idCategoria, categoria, slug, activa, createdAt, subcategorias: [...] }], estadisticas: {...} }
      if (data?.success && Array.isArray(data?.categorias)) {
        // Opcional: ordenar por nombre
        const ordered = [...data.categorias].sort((a, b) =>
          String(a.categoria).localeCompare(String(b.categoria), 'es', { sensitivity: 'base' })
        );
        setCategories(ordered);
      } else {
        setCategories([]);
        showToast('No se recibieron categorías desde el servidor', 'warning');
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      showToast('Error al cargar categorías', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Si tu API de categoriasGet ya devuelve subcategorias, puedes usarlas directo.
  // Este fetch es por si quieres "refrescar" subcategorías en el modal.
  const loadSubcategories = async (idCategoria) => {
    try {
      const res = await fetch(`${baseURL}/subcategoriasGet.php?idCategoria=${idCategoria}`);
      const data = await res.json();
      setSubcategories(data.subcategorias || []);
    } catch (err) {
      console.error('Error loading subcategories:', err);
      showToast('Error al cargar subcategorías', 'error');
      setSubcategories([]);
    }
  };

  const deleteCategory = (idCategoria, nombreCategoria) => {
    Swal.fire({
      title: '¿Eliminar categoría?',
      text: `Se eliminará "${nombreCategoria}" y todas sus subcategorías`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        const res = await fetch(`${baseURL}/categoriaDelete.php?idCategoria=${idCategoria}`, { method: 'DELETE' });
        const data = await res.json();

        if (data?.mensaje) {
          showToast('Categoría eliminada exitosamente', 'success');
          loadCategories();
        } else {
          showToast(data?.error || 'No se pudo eliminar la categoría', 'error');
        }
      } catch (err) {
        console.error('Error deleting category:', err);
        showToast('Error al eliminar la categoría', 'error');
      }
    });
  };

  const openModal = async (categoryItem) => {
    setActiveCategory(categoryItem);
    setNewCategoryName(categoryItem.categoria || '');

    // Si ya tienes subcategorías embebidas, úsalas como estado inicial:
    setSubcategories(Array.isArray(categoryItem.subcategorias) ? categoryItem.subcategorias : []);
    setModalVisible(true);

    // Si prefieres recargar desde el endpoint (descomenta):
    // await loadSubcategories(categoryItem.idCategoria);
  };

  const closeModal = () => {
    setModalVisible(false);
    setNewCategoryName('');
    setNewSubcategory('');
    setActiveCategory({});
    setSubcategories([]);
  };

  const saveCategoryName = async (idCategoria) => {
    if (!newCategoryName.trim()) {
      showToast('El nombre de la categoría no puede estar vacío', 'warning');
      return;
    }
    if (newCategoryName.length < 2) {
      showToast('La categoría debe tener al menos 2 caracteres', 'error');
      return;
    }

    try {
      const res = await fetch(`${baseURL}/categoriaPut.php?idCategoria=${idCategoria}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoria: newCategoryName.trim() }),
      });
      const data = await res.json();

      if (data?.error) {
        showToast(data.error, 'error');
      } else {
        showToast(data?.mensaje || 'Categoría actualizada exitosamente', 'success');
        await loadCategories();
        closeModal();
      }
    } catch (err) {
      console.error('Error updating category:', err);
      showToast('Error al actualizar la categoría', 'error');
    }
  };

  const addSubcategory = async () => {
    const name = newSubcategory.trim();
    if (!name) {
      showToast('Ingrese el nombre de la subcategoría', 'warning');
      return;
    }
    if (name.length < 2) {
      showToast('La subcategoría debe tener al menos 2 caracteres', 'error');
      return;
    }
    if (subcategories.some(s => String(s.subcategoria).toLowerCase() === name.toLowerCase())) {
      showToast('Esta subcategoría ya existe', 'warning');
      return;
    }

    try {
      const res = await fetch(`${baseURL}/subcategoriaPost.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subcategoria: name,
          idCategoria: activeCategory.idCategoria
        }),
      });
      const data = await res.json();

      if (data?.error) {
        showToast(data.error, 'error');
      } else {
        showToast('Subcategoría agregada exitosamente', 'success');
        setNewSubcategory('');
        // refrescar lista
        await loadSubcategories(activeCategory.idCategoria);
        // también refrescar categorías para que la tabla muestre el conteo correcto
        await loadCategories();
      }
    } catch (err) {
      console.error('Error adding subcategory:', err);
      showToast('Error al agregar la subcategoría', 'error');
    }
  };

  const deleteSubcategory = async (idSubcategoria, nombreSubcategoria) => {
    Swal.fire({
      title: '¿Eliminar subcategoría?',
      text: `Se eliminará "${nombreSubcategoria}"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        const res = await fetch(`${baseURL}/subcategoriaDelete.php?idSubcategoria=${idSubcategoria}`, {
          method: 'DELETE',
        });
        const data = await res.json();

        if (data?.mensaje) {
          showToast('Subcategoría eliminada exitosamente', 'success');
          await loadSubcategories(activeCategory.idCategoria);
          await loadCategories();
        } else {
          showToast(data?.error || 'No se pudo eliminar la subcategoría', 'error');
        }
      } catch (err) {
        console.error('Error deleting subcategory:', err);
        showToast('Error al eliminar la subcategoría', 'error');
      }
    });
  };

  const onSubcategoryKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSubcategory();
    }
  };

  const getSubcategoryCount = (cat) => Array.isArray(cat.subcategorias) ? cat.subcategorias.length : 0;

  const refreshData = () => {
    loadCategories();
    showToast('Datos actualizados', 'info');
  };

  // === Render ===
  return (
    <div>
      <CustomToast toasts={toasts} />

      {/* Header */}
      <div className="global-component-header" style={{ padding: '12px 16px' }}>
        <div className="flex-between">
          <div className="global-component-title">
            <FontAwesomeIcon icon={faFolderOpen} />
            Gestión de Categorías
          </div>

          <div className="flex-center" style={{ gap: '12px' }}>
            <div className="global-stat-card compact" style={{ marginLeft: '10px' }}>
              <span className="global-stat-number">{categories.length}</span>
              <span className="global-stat-label">Categorías</span>
            </div>

            <div className="global-stat-card compact">
              <span className="global-stat-number">
                {categories.reduce((acc, c) => acc + getSubcategoryCount(c), 0)}
              </span>
              <span className="global-stat-label">Subcategorías</span>
            </div>

            <button onClick={refreshData} className="btn-product-secondary compact flex-center" title="Actualizar">
              <FontAwesomeIcon icon={faSync} />
            </button>
          </div>
        </div>
      </div>

      {/* Crear nueva categoría */}
      <NewCategoria />

      {/* Modal */}
      {modalVisible && (
        <div className="product-modal-backdrop">
          <div className="product-modal-container" style={{ maxWidth: '700px' }}>
            <div className="product-modal-header">
              <div className="product-modal-title">
                <FontAwesomeIcon icon={faEdit} size={24} />
                Editar Categoría
              </div>
              <button className="product-close-button" onClick={closeModal}>
                <FontAwesomeIcon icon={faTimes} size={20} />
              </button>
            </div>

            <div className="product-modal-body">
              {/* Nombre */}
              <div className="product-section-card mb-24">
                <div className="product-section-header">
                  <div className="product-section-title">
                    <FontAwesomeIcon icon={faFolderOpen} size={20} />
                    Nombre de la Categoría
                  </div>
                </div>

                <div className="product-section-content">
                  <div className="product-form-group">
                    <label className="product-form-label">
                      <FontAwesomeIcon icon={faEdit} size={16} />
                      Cambiar nombre *
                    </label>
                    <div className="input-with-button">
                      <input
                        type="text"
                        className={`product-form-input ${newCategoryName.length > 80 ? 'input-error' : newCategoryName.length > 10 ? 'input-success' : ''}`}
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="Ingrese el nuevo nombre de la categoría"
                        maxLength={100}
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => saveCategoryName(activeCategory.idCategoria)}
                        className="btn-product-primary"
                        disabled={
                          !newCategoryName.trim() ||
                          newCategoryName.length < 2 ||
                          newCategoryName.trim() === activeCategory.categoria
                        }
                        style={{ marginLeft: '12px', whiteSpace: 'nowrap' }}
                      >
                        Guardar Nombre
                      </button>
                    </div>
                    <div className={`character-counter ${newCategoryName.length >= 90 ? 'error' : newCategoryName.length >= 70 ? 'warning' : ''}`}>
                      {newCategoryName.length}/100 caracteres
                    </div>
                    {newCategoryName.length > 0 && newCategoryName.length < 2 && (
                      <div className="form-hint error">Mínimo 2 caracteres requeridos</div>
                    )}
                    {newCategoryName === activeCategory.categoria && (
                      <div className="form-hint">El nombre no ha cambiado</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Subcategorías */}
              <div className="product-section-card animate-stagger-1">
                <div className="product-section-header">
                  <div className="product-section-title">
                    <FontAwesomeIcon icon={faSitemap} size={20} />
                    Subcategorías de "{activeCategory.categoria}"
                  </div>
                  <div className="product-section-badge">
                    {subcategories.length} subcategorías
                  </div>
                </div>

                <div className="product-section-content">
                  <label className="product-form-label">
                    <FontAwesomeIcon icon={faInfoCircle} size={14} />
                    Las subcategorías se guardan automáticamente al agregarlas o eliminarlas
                  </label>

                  <div className="product-custom-size-form mb-20">
                    <input
                      type="text"
                      className="product-form-input"
                      value={newSubcategory}
                      onChange={(e) => setNewSubcategory(e.target.value)}
                      onKeyPress={onSubcategoryKeyPress}
                      placeholder="Agregar nueva subcategoría"
                      maxLength={100}
                    />
                    <button
                      type="button"
                      onClick={addSubcategory}
                      className="btn-product-primary"
                      disabled={!newSubcategory.trim()}
                    >
                      Agregar
                    </button>
                  </div>

                  {newSubcategory.length > 0 && (
                    <div className={`character-counter ${newSubcategory.length >= 90 ? 'error' : newSubcategory.length >= 70 ? 'warning' : ''}`}>
                      {newSubcategory.length}/100 caracteres
                    </div>
                  )}

                  {subcategories.length > 0 ? (
                    <div className="product-sizes-list mt-20">
                      <div className="subcategories-grid">
                        {subcategories.map((sub, idx) => (
                          <div
                            key={sub.idSubcategoria ?? `${sub.subcategoria}-${idx}`}
                            className="subcategory-item"
                            style={{ animationDelay: `${idx * 0.1}s` }}
                          >
                            <div className="subcategory-content">
                              <FontAwesomeIcon icon={faSitemap} size={16} />
                              {sub.subcategoria}
                            </div>
                            {sub.idSubcategoria && (
                              <button
                                type="button"
                                onClick={() => deleteSubcategory(sub.idSubcategoria, sub.subcategoria)}
                                className="btn-product-delete"
                                title="Eliminar subcategoría (se guarda automáticamente)"
                                style={{ padding: '6px', minWidth: '32px', minHeight: '32px', borderRadius: '6px' }}
                              >
                                <FontAwesomeIcon icon={faTrash} size={14} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="product-empty-state mt-20">
                      <FontAwesomeIcon icon={faSitemap} size={48} />
                      <div className="empty-title">No hay subcategorías</div>
                      <div className="empty-description">Agrega subcategorías para organizar mejor esta categoría</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="product-submit-section">
              <button type="button" onClick={closeModal} className="btn-product-secondary">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabla */}
      <div className="global-table-container">
        {loading ? (
          <div className="global-loading-state">
            <FontAwesomeIcon icon={faSpinner} size="2x" />
            <div>Cargando categorías...</div>
          </div>
        ) : categories.length > 0 ? (
          <table className="global-table">
            <thead>
              <tr>
                <th>
                  <FontAwesomeIcon icon={faFolderOpen} style={{ marginRight: '8px' }} />
                  Categoría
                </th>
                <th>
                  <FontAwesomeIcon icon={faSitemap} style={{ marginRight: '8px' }} />
                  Subcategorías
                </th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat, idx) => (
                <tr key={cat.idCategoria} style={{ animationDelay: `${idx * 0.05}s` }}>
                  <td>
                    <strong>{cat.categoria}</strong>
                    {getSubcategoryCount(cat) > 0 && (
                      <span className="global-indicator success">
                        <FontAwesomeIcon icon={faSitemap} />
                        {getSubcategoryCount(cat)}
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      style={{
                        color: getSubcategoryCount(cat) > 0 ? 'var(--color2)' : 'var(--text-color2)',
                        fontWeight: getSubcategoryCount(cat) > 0 ? '600' : '400'
                      }}
                    >
                      {getSubcategoryCount(cat) > 0
                        ? `${getSubcategoryCount(cat)} subcategorías`
                        : 'Sin subcategorías'}
                    </span>
                  </td>
                  <td>
                    <div className="global-table-actions">
                      <button className="global-btn-edit" onClick={() => openModal(cat)} title="Editar">
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="global-btn-delete"
                        onClick={() => deleteCategory(cat.idCategoria, cat.categoria)}
                        title="Eliminar categoría"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="global-empty-state">
            <FontAwesomeIcon icon={faFolderOpen} size="3x" />
            <div className="global-empty-title">No hay categorías creadas</div>
            <div className="global-empty-description">
              Comienza creando tu primera categoría usando el botón "Nueva Categoría" de arriba.
              Las categorías te ayudarán a organizar mejor tus productos.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
